import {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {getLocalTaxesIncluded, getTaxesIncluded} from 'modules/meta/selectors'
import {getHotelOfferEntityOffers} from 'modules/sapiSearch/selectors'
import {RootState} from 'store'
import {getCheapestTopOfferByNightlyRate, getNumberOfNights} from 'utils/offers'

import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'

interface UseTaxAdjustmentParams {
  hotelId?: string
  checkIn?: string
  checkOut?: string
}

interface UseTaxAdjustmentReturn {
  /** The selected offer used for tax estimation (cheapest offer by nightly rate) */
  selectedOffer: SearchOffer | undefined
  /** Whether taxes should be included in displayed prices */
  includeTaxes: boolean
  /** Whether local taxes (hotel fees) should be included in displayed prices */
  includeLocalTaxes: boolean
  /** Estimated total taxes from the selected offer */
  estimatedTaxes: number
  /** Estimated total local taxes (hotel fees) from the selected offer */
  estimatedLocalTaxes: number
  /** Number of nights for the stay */
  numberOfNights: number
  /**
   * Adjusts a total price by subtracting taxes based on user preferences.
   * Use this for historical prices that come WITH taxes from the API.
   * @param totalPrice - The total price (with taxes) to adjust
   * @returns The adjusted total price
   */
  adjustTotalPrice: (totalPrice: number) => number
  /**
   * Adjusts a nightly price by subtracting taxes based on user preferences.
   * Use this for nightly averages that come WITH taxes from the API.
   * @param nightlyPrice - The nightly price (with taxes) to adjust
   * @returns The adjusted nightly price
   */
  adjustNightlyPrice: (nightlyPrice: number) => number
}

/**
 * Shared hook for tax adjustment logic used by PriceTrend's nightly-mode
 * display path. Centralises offer selection (cheapest top offer's rate
 * breakdown) and tax-pref-aware subtraction.
 *
 * Only consumed by `usePriceHistory` for the nightly branch — total mode
 * intentionally bypasses this hook to keep the chart aligned with OfferBox's
 * full-tax "total price with taxes & fees" display.
 *
 * @param params - Hotel and date parameters for tax estimation
 * @returns Tax adjustment utilities and values
 */
export const useTaxAdjustment = (
  params?: UseTaxAdjustmentParams
): UseTaxAdjustmentReturn => {
  const {hotelId, checkIn, checkOut} = params || {}

  const includeTaxes = useSelector(getTaxesIncluded)
  const includeLocalTaxes = useSelector(getLocalTaxesIncluded)

  const offers = useSelector((state: RootState) =>
    hotelId ? getHotelOfferEntityOffers(state, String(hotelId)) : undefined
  )

  // Always use the cheapest offer for consistent tax estimation
  const selectedOffer = useMemo(() => {
    if (!offers || offers.length === 0) {
      return undefined
    }

    return getCheapestTopOfferByNightlyRate(offers)
  }, [offers])

  const estimatedTaxes = selectedOffer?.rate?.taxes ?? 0
  const estimatedLocalTaxes = selectedOffer?.rate?.hotelFees ?? 0

  // Stay length, guarded for missing/invalid dates so downstream per-night
  // math never hits divide-by-zero or NaN. `getNumberOfNights` returns NaN
  // when dates are empty (its `= ''` defaults parse to Invalid Date), so the
  // finite-check substitutes 1.
  const numberOfNights = useMemo(() => {
    const nights = getNumberOfNights(checkIn, checkOut)
    return Number.isFinite(nights) && nights > 0 ? nights : 1
  }, [checkIn, checkOut])

  // Calculate the tax amounts to subtract based on user preferences
  const taxesToSubtract = useMemo(() => {
    if (!selectedOffer) {
      return {total: 0, nightly: 0}
    }

    const totalTaxes = includeTaxes ? 0 : estimatedTaxes
    const totalLocalTaxes = includeLocalTaxes ? 0 : estimatedLocalTaxes
    const total = totalTaxes + totalLocalTaxes
    const nightly = total / numberOfNights

    return {total, nightly}
  }, [
    selectedOffer,
    includeTaxes,
    includeLocalTaxes,
    estimatedTaxes,
    estimatedLocalTaxes,
    numberOfNights
  ])

  // Memoized adjustment functions
  const adjustTotalPrice = useMemo(
    () => (totalPrice: number) =>
      Math.max(0, totalPrice - taxesToSubtract.total),
    [taxesToSubtract.total]
  )

  const adjustNightlyPrice = useMemo(
    () => (nightlyPrice: number) =>
      Math.max(0, nightlyPrice - taxesToSubtract.nightly),
    [taxesToSubtract.nightly]
  )

  return {
    selectedOffer,
    includeTaxes,
    includeLocalTaxes,
    estimatedTaxes,
    estimatedLocalTaxes,
    numberOfNights,
    adjustTotalPrice,
    adjustNightlyPrice
  }
}
